import { SparklesIcon } from "lucide-react";
import { useMemo } from "react";
import { cn } from "@/lib/utils";

interface UITreeElement {
  key?: string;
  type?: string;
  props?: Record<string, unknown>;
  children?: string[];
}

interface UITree {
  root: string;
  elements: Record<string, UITreeElement>;
}

interface AIAnalysisPreviewProps {
  aiAnalysis?: {
    ui_tree?: UITree;
    analyzed_at?: string;
  };
  compact?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * AIAnalysisPreview displays a summary of AI analysis in memo list view.
 * Shows the first MetricCard value as a preview (e.g., "PE: 28.5").
 */
export function AIAnalysisPreview({ aiAnalysis, compact, className, onClick }: AIAnalysisPreviewProps) {
  const preview = useMemo(() => {
    if (!aiAnalysis?.ui_tree) {
      return null;
    }

    const { ui_tree } = aiAnalysis;
    const elements = ui_tree.elements || {};

    // Find the first MetricCard component in the tree
    const rootElement = elements[ui_tree.root];
    if (!rootElement || !Array.isArray(rootElement.children)) {
      return null;
    }

    // Recursively find first MetricCard
    function findMetricCard(elementKey: string): UITreeElement | null {
      const element = elements[elementKey];
      if (!element) return null;

      if (element.type === "MetricCard") {
        return element;
      }

      if (Array.isArray(element.children)) {
        for (const childKey of element.children) {
          const result = findMetricCard(childKey);
          if (result) return result;
        }
      }

      return null;
    }

    const metricCard = findMetricCard(rootElement.key || ui_tree.root);
    if (!metricCard) {
      return null;
    }

    // Extract label and value from props
    const props = metricCard.props || {};
    const label = typeof props.label === "string" ? props.label : "";
    const value = typeof props.value === "string" || typeof props.value === "number" ? String(props.value) : "";

    return { label, value };
  }, [aiAnalysis]);

  if (!preview) {
    return null;
  }

  const { label, value } = preview;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 text-muted-foreground",
        "text-xs leading-tight",
        "cursor-pointer hover:text-foreground transition-colors",
        "bg-transparent border-0 p-0 text-left",
        onClick ? "hover:underline decoration-primary/30 underline-offset-2" : "",
        compact ? "mt-2" : "mt-1.5",
        className,
      )}
    >
      <SparklesIcon className="w-3 h-3 flex-shrink-0 text-primary" />
      <span className="font-medium truncate">
        {label}: {value}
      </span>
    </button>
  );
}
